/**
 * Settings Page Component
 *
 * Multi-app settings with MAIA, Calendar, Email, and Phone apps
 * Each app has its own floating navigation sidebar and settings sections
 *
 * NOTE: Calendar, Email, and Phone settings are DUMMY implementations
 * for demonstration purposes. They simulate real integrations but
 * do not actually connect to external services.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBrandName, usePrivacyEmail, useTenant } from '../contexts/TenantContext';
import { useUiMode } from '../contexts/UiModeContext';
import {
  getProfile,
  updateProfile,
  changePassword,
  getUserPreferences,
  updateUserPreferences,
  getBillingInfo,
  updateBillingInfo,
  getGDPRConsents,
  updateGDPRConsents,
  requestGDPRDataExport,
  getGDPRDataExports,
  getGDPRDeletionRequest,
  requestGDPRAccountDeletion,
  cancelGDPRDeletionRequest,
  getAvailableLanguages,
  getLanguagePreferences,
  updateLanguagePreferences,
  getPreferredLanguage,
  updatePreferredLanguage,
  // Sync API imports
  getSyncAccounts,
  createSyncAccount,
  deleteSyncAccount,
  triggerSync,
  // Avatar API imports
  requestAvatarUploadUrl,
  confirmAvatarUpload,
  deleteAvatar,
  type UserProfile,
  type ProfileUpdateRequest,
  type BillingInfoUpdate,
  type GDPRConsent,
  type GDPRDataExport,
  type GDPRDeletionRequest,
  type LanguageInfo,
  type SyncAccount,
  type CreateSyncAccountRequest,
} from '../services/api';
import type { NoiseReductionType } from '../types/user';
import type { Avatar } from '../types/avatar';
import {
  listAvatars,
  getMyAvatarSelection,
  setMyAvatarSelection,
} from '../services/avatarCatalogApi';
import EmailAccountSetup from './modules/EmailAccountSetup';
import emailApi, { EmailAccount } from '../services/emailApi';
import { useLanguageSync } from '../hooks/useLanguageSync';
import { useTranslation } from 'react-i18next';
import UserAvatar from './UserAvatar';
import AvatarCropModal from './AvatarCropModal';
import axios from 'axios';

// App definitions
type AppId = 'maia' | 'calendar' | 'email' | 'phone';

interface AppDefinition {
  id: AppId;
  label: string;
  icon: string;
  color: string;
  bgColor: string;
}

const getApps = (brandName: string): AppDefinition[] => [
  { id: 'maia', label: brandName, icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z', color: 'text-purple-600', bgColor: 'bg-purple-50' },
  { id: 'calendar', label: 'Calendar', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', color: 'text-blue-600', bgColor: 'bg-blue-50' },
  { id: 'email', label: 'Email', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', color: 'text-green-600', bgColor: 'bg-green-50' },
  { id: 'phone', label: 'Phone', icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z', color: 'text-orange-600', bgColor: 'bg-orange-50' },
];

// Section definitions per app
interface SectionDefinition {
  id: string;
  label: string;
  icon: string;
}

const sectionsByApp: Record<AppId, SectionDefinition[]> = {
  maia: [
    { id: 'profile', label: 'Profile', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
    { id: 'password', label: 'Password', icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' },
    { id: 'voice', label: 'Voice', icon: 'M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z' },
    { id: 'billing', label: 'Billing', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
    { id: 'gdpr', label: 'Privacy', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
    { id: 'account', label: 'Account', icon: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' },
  ],
  calendar: [
    { id: 'cal-accounts', label: 'Connected', icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1' },
    { id: 'cal-sync', label: 'Sync', icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' },
    { id: 'cal-notifications', label: 'Alerts', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' },
    { id: 'cal-availability', label: 'Availability', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
  ],
  email: [
    { id: 'email-accounts', label: 'Accounts', icon: 'M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207' },
    { id: 'email-imap', label: 'IMAP/SMTP', icon: 'M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01' },
    { id: 'email-signatures', label: 'Signatures', icon: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z' },
    { id: 'email-filters', label: 'Filters', icon: 'M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z' },
  ],
  phone: [
    { id: 'phone-numbers', label: 'Numbers', icon: 'M7 20l4-16m2 16l4-16M6 9h14M4 15h14' },
    { id: 'phone-twilio', label: 'Twilio', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z' },
    { id: 'phone-voicemail', label: 'Voicemail', icon: 'M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z' },
    { id: 'phone-routing', label: 'Routing', icon: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7' },
  ],
};

// ============================================================================
// DATA TYPES FOR PHONE APP (DUMMY)
// Calendar and Email now use real CalDAV/IMAP sync via SyncAccount type
// Phone is still a placeholder implementation
// ============================================================================

interface DummyPhoneNumber {
  id: string;
  number: string;
  country: string;
  type: 'local' | 'toll-free' | 'mobile';
  status: 'active' | 'pending';
}

// Modal state for adding sync accounts
interface AddAccountModalState {
  isOpen: boolean;
  accountType: 'caldav' | 'imap' | null;
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const brandName = useBrandName();
  const privacyEmail = usePrivacyEmail();
  const { tenant } = useTenant();
  const { effectiveMode } = useUiMode();
  const isEmployeeMode = effectiveMode === 'employee';
  const isInstitutionalTenant = (tenant as { tenant_type?: string } | null)?.tenant_type === 'institutional';
  const { changeLanguage, currentLanguage } = useLanguageSync();
  const { t } = useTranslation('settings');

  // Translation lookups for app and section labels
  const appLabelKeys: Record<AppId, string> = { maia: '', calendar: 'apps.calendar', email: 'apps.email', phone: 'apps.phone' };
  const sectionLabelKeys: Record<string, string> = {
    profile: 'sections.profile', password: 'sections.password', voice: 'sections.voice',
    billing: 'sections.billing', gdpr: 'sections.privacy', account: 'sections.account',
    'cal-accounts': 'sections.connected', 'cal-sync': 'sections.sync',
    'cal-notifications': 'sections.alerts', 'cal-availability': 'sections.availability',
    'email-accounts': 'sections.accounts', 'email-imap': 'sections.imapSmtp',
    'email-signatures': 'sections.signatures', 'email-filters': 'sections.filters',
    'phone-numbers': 'sections.numbers', 'phone-twilio': 'sections.integration',
    'phone-voicemail': 'sections.voicemail', 'phone-routing': 'sections.routing',
  };
  const getAppLabel = (app: AppDefinition): string => app.id === 'maia' ? app.label : t(appLabelKeys[app.id]);
  const getSectionLabel = (section: SectionDefinition): string => sectionLabelKeys[section.id] ? t(sectionLabelKeys[section.id]) : section.label;
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Generate apps array with brand name
  const apps = useMemo(() => getApps(brandName), [brandName]);

  // Current app selection
  const [activeApp, setActiveApp] = useState<AppId>('maia');
  const [activeSection, setActiveSection] = useState('profile');
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);

  // MAIA Profile state
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Avatar state
  const [showAvatarCrop, setShowAvatarCrop] = useState(false);
  const [selectedAvatarFile, setSelectedAvatarFile] = useState<File | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const avatarFileInputRef = useRef<HTMLInputElement>(null);

  // Talking-head avatar catalog selection (separate from profile picture)
  const [catalogAvatars, setCatalogAvatars] = useState<Avatar[]>([]);
  const [selectedCatalogAvatarId, setSelectedCatalogAvatarId] = useState<number | null>(null);
  const [isSavingCatalogAvatar, setIsSavingCatalogAvatar] = useState(false);

  // MAIA Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // MAIA Preferences state
  const [noiseReduction, setNoiseReduction] = useState<NoiseReductionType>('far_field');
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);

  // Language preferences state
  const [availableLanguages, setAvailableLanguages] = useState<LanguageInfo[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(['en']);
  const [isSavingLanguages, setIsSavingLanguages] = useState(false);
  const [languageMessage, setLanguageMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Preferred language state (default voice AI language)
  const [preferredLanguage, setPreferredLanguage] = useState<string>('en');
  const [isSavingPreferredLanguage, setIsSavingPreferredLanguage] = useState(false);
  const [preferredLanguageMessage, setPreferredLanguageMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // MAIA Billing info state
  const [companyName, setCompanyName] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('Malta');
  const [vatNumber, setVatNumber] = useState('');
  const [taxId, setTaxId] = useState('');
  const [isSavingBilling, setIsSavingBilling] = useState(false);
  const [billingMessage, setBillingMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // ============================================================================
  // MAIA GDPR State (Privacy & Data Protection)
  // Full GDPR compliance: consent management, data access, export, deletion
  // ============================================================================
  const [gdprConsents, setGdprConsents] = useState<GDPRConsent | null>(null);
  const [gdprExports, setGdprExports] = useState<GDPRDataExport[]>([]);
  const [gdprDeletionRequest, setGdprDeletionRequest] = useState<GDPRDeletionRequest | null>(null);
  const [isSavingGdpr, setIsSavingGdpr] = useState(false);
  const [gdprMessage, setGdprMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [isExportingData, setIsExportingData] = useState(false);

  // ============================================================================
  // REAL STATE FOR CALENDAR APP (CalDAV Sync)
  // Uses SyncAccount type for real CalDAV server connections
  // ============================================================================
  const [calendarAccounts, setCalendarAccounts] = useState<SyncAccount[]>([]);
  const [isLoadingCalAccounts, setIsLoadingCalAccounts] = useState(true);
  const [calSyncInterval, setCalSyncInterval] = useState('15');
  const [calNotifyBefore, setCalNotifyBefore] = useState('10');
  const [calDefaultDuration, setCalDefaultDuration] = useState('30');
  const [calWorkingHoursStart, setCalWorkingHoursStart] = useState('09:00');
  const [calWorkingHoursEnd, setCalWorkingHoursEnd] = useState('17:00');
  const [calMessage, setCalMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isSyncingCal, setIsSyncingCal] = useState<string | null>(null);

  // ============================================================================
  // REAL STATE FOR EMAIL APP (IMAP/SMTP Sync)
  // Uses aegis5 EmailAccount type for real IMAP/SMTP server connections
  // ============================================================================
  const [emailAccounts, setEmailAccounts] = useState<EmailAccount[]>([]);
  const [showAddEmailModal, setShowAddEmailModal] = useState(false);
  const [isLoadingEmailAccounts, setIsLoadingEmailAccounts] = useState(true);
  const [emailSignature, setEmailSignature] = useState(`Best regards,\n${user?.full_name || 'User'}`);
  const [emailAutoReply, setEmailAutoReply] = useState(false);
  const [emailAutoReplyMessage, setEmailAutoReplyMessage] = useState('');
  const [emailMessage, setEmailMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isSyncingEmail, setIsSyncingEmail] = useState<string | null>(null);

  // Modal state for adding new sync accounts
  const [addAccountModal, setAddAccountModal] = useState<AddAccountModalState>({ isOpen: false, accountType: null });
  const [newAccountForm, setNewAccountForm] = useState<CreateSyncAccountRequest>({
    account_type: 'caldav',
    display_name: '',
    server_host: '',
    server_port: 443,
    username: '',
    password: '',
    use_ssl: true,
    is_enabled: true,
  });
  const [isAddingAccount, setIsAddingAccount] = useState(false);

  // ============================================================================
  // DUMMY STATE FOR PHONE APP
  // This simulates Twilio integration but doesn't actually connect
  // ============================================================================
  const [phoneNumbers, setPhoneNumbers] = useState<DummyPhoneNumber[]>([
    { id: 'phone-1', number: '+1 (555) 123-4567', country: 'US', type: 'local', status: 'active' },
  ]);
  const [twilioAccountSid, setTwilioAccountSid] = useState('');
  const [twilioAuthToken, setTwilioAuthToken] = useState('');
  const [twilioConnected, setTwilioConnected] = useState(false);
  const [voicemailEnabled, setVoicemailEnabled] = useState(true);
  const [voicemailGreeting, setVoicemailGreeting] = useState('default');
  const [callForwardingEnabled, setCallForwardingEnabled] = useState(false);
  const [callForwardingNumber, setCallForwardingNumber] = useState('');
  const [phoneMessage, setPhoneMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Close user menu when clicking outside
  useEffect(() => {
    if (!userMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  // Load MAIA profile, preferences, billing info, GDPR data, and language preferences on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [profileData, prefsData, billingData, gdprData, gdprExportsData, gdprDeletionData, languagesData, langPrefsData, prefLangData] = await Promise.all([
          getProfile(),
          getUserPreferences(),
          getBillingInfo(),
          getGDPRConsents(),
          getGDPRDataExports(),
          getGDPRDeletionRequest(),
          getAvailableLanguages(),
          getLanguagePreferences(),
          getPreferredLanguage()
        ]);
        setProfile(profileData.profile);
        setFullName(profileData.profile.full_name);
        setEmail(profileData.profile.email);
        setNoiseReduction(prefsData.noise_reduction);

        const info = billingData.billing_info;
        setCompanyName(info.company_name || '');
        setAddressLine1(info.address_line1 || '');
        setAddressLine2(info.address_line2 || '');
        setCity(info.city || '');
        setPostalCode(info.postal_code || '');
        setCountry(info.country || 'Malta');
        setVatNumber(info.vat_number || '');
        setTaxId(info.tax_id || '');

        // GDPR data
        setGdprConsents(gdprData);
        setGdprExports(gdprExportsData.exports || []);
        setGdprDeletionRequest(gdprDeletionData.request);

        // Language preferences
        setAvailableLanguages(languagesData);
        setSelectedLanguages(langPrefsData);
        setPreferredLanguage(prefLangData);

        // Talking-head avatar catalog (only if avatar feature is enabled for this user)
        if (user?.avatar_enabled) {
          try {
            const [allAvatars, mySelection] = await Promise.all([
              listAvatars(),
              getMyAvatarSelection(),
            ]);
            setCatalogAvatars(allAvatars);
            setSelectedCatalogAvatarId(mySelection.selected_avatar_id);
          } catch (avatarErr) {
            console.error('Failed to load avatar catalog:', avatarErr);
          }
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
      } finally {
        setIsLoadingProfile(false);
      }
    };
    loadData();
  }, []);

  // Load sync accounts when calendar or email app is selected
  useEffect(() => {
    const loadSyncAccounts = async () => {
      if (activeApp === 'calendar') {
        setIsLoadingCalAccounts(true);
        try {
          const accounts = await getSyncAccounts('caldav');
          setCalendarAccounts(accounts);
        } catch (error) {
          console.error('Failed to load CalDAV accounts:', error);
          setCalMessage({ type: 'error', text: 'Failed to load calendar accounts' });
        } finally {
          setIsLoadingCalAccounts(false);
        }
      } else if (activeApp === 'email') {
        setIsLoadingEmailAccounts(true);
        try {
          // Use aegis5 email API instead of sync API
          const accounts = await emailApi.accounts.list();
          setEmailAccounts(accounts);
        } catch (error) {
          console.error('Failed to load email accounts:', error);
          setEmailMessage({ type: 'error', text: 'Failed to load email accounts' });
        } finally {
          setIsLoadingEmailAccounts(false);
        }
      }
    };
    loadSyncAccounts();
  }, [activeApp]);

  // Update active section when app changes
  useEffect(() => {
    const sections = sectionsByApp[activeApp];
    if (sections.length > 0) {
      setActiveSection(sections[0].id);
    }
  }, [activeApp]);

  // Scroll tracking for active section
  useEffect(() => {
    const handleScroll = () => {
      const sections = sectionsByApp[activeApp];
      const scrollPosition = window.scrollY + 150;

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeApp]);

  // Scroll to section function
  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  }, []);

  // Avatar handlers
  const handleAvatarSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setProfileMessage({ type: 'error', text: 'Please select a JPG, PNG, or WebP image.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setProfileMessage({ type: 'error', text: 'Image must be under 5MB.' });
      return;
    }

    setSelectedAvatarFile(file);
    setShowAvatarCrop(true);
    // Reset file input so the same file can be re-selected
    if (avatarFileInputRef.current) avatarFileInputRef.current.value = '';
  };

  const handleAvatarCropComplete = async (blob: Blob) => {
    setShowAvatarCrop(false);
    setSelectedAvatarFile(null);
    setIsUploadingAvatar(true);
    setProfileMessage(null);

    try {
      // 1. Get presigned URL
      const { upload_url, s3_key } = await requestAvatarUploadUrl('image/webp', blob.size);

      // 2. PUT to S3 directly
      await axios.put(upload_url, blob, {
        headers: { 'Content-Type': 'image/webp' },
      });

      // 3. Confirm upload
      const { avatar_url } = await confirmAvatarUpload(s3_key);

      // Update local state + auth context so all components see it immediately
      setProfile((prev) => prev ? { ...prev, avatar_url } : prev);
      updateUser({ avatar_url });
      setProfileMessage({ type: 'success', text: 'Profile picture updated.' });
    } catch (err) {
      console.error('Avatar upload failed:', err);
      setProfileMessage({ type: 'error', text: 'Failed to upload profile picture.' });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleAvatarDelete = async () => {
    setIsUploadingAvatar(true);
    setProfileMessage(null);
    try {
      await deleteAvatar();
      setProfile((prev) => prev ? { ...prev, avatar_url: undefined } : prev);
      updateUser({ avatar_url: null });
      setProfileMessage({ type: 'success', text: 'Profile picture removed.' });
    } catch (err) {
      console.error('Avatar delete failed:', err);
      setProfileMessage({ type: 'error', text: 'Failed to remove profile picture.' });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  // MAIA handlers
  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingProfile(true);
    setProfileMessage(null);

    try {
      const updates: ProfileUpdateRequest = {};
      if (fullName !== profile?.full_name) updates.full_name = fullName;
      if (email !== profile?.email) updates.email = email;

      if (Object.keys(updates).length === 0) {
        setProfileMessage({ type: 'error', text: t('profile.noChanges') });
        return;
      }

      await updateProfile(updates);
      setProfileMessage({ type: 'success', text: t('profile.success') });
    } catch (error) {
      setProfileMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to update profile' });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingPassword(true);
    setPasswordMessage(null);

    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: t('password.errors.mismatch') });
      setIsSavingPassword(false);
      return;
    }

    if (newPassword.length < 8) {
      setPasswordMessage({ type: 'error', text: t('password.errors.tooShort') });
      setIsSavingPassword(false);
      return;
    }

    try {
      await changePassword({ current_password: currentPassword, new_password: newPassword });
      setPasswordMessage({ type: 'success', text: t('password.success') });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setPasswordMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to change password' });
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleNoiseReductionChange = async (value: NoiseReductionType) => {
    setIsSavingPreferences(true);
    try {
      await updateUserPreferences(value);
      setNoiseReduction(value);
    } catch (error) {
      console.error('Failed to update preferences:', error);
    } finally {
      setIsSavingPreferences(false);
    }
  };

  const handleCatalogAvatarChange = async (newId: number | null) => {
    setIsSavingCatalogAvatar(true);
    try {
      await setMyAvatarSelection(newId);
      setSelectedCatalogAvatarId(newId);
    } catch (error) {
      console.error('Failed to update avatar selection:', error);
      alert(error instanceof Error ? error.message : 'Failed to update avatar selection');
    } finally {
      setIsSavingCatalogAvatar(false);
    }
  };

  const handleLanguageToggle = async (langCode: string) => {
    setLanguageMessage(null);
    const newSelection = selectedLanguages.includes(langCode)
      ? selectedLanguages.filter(l => l !== langCode)
      : [...selectedLanguages, langCode];

    // Ensure at least one language is selected
    if (newSelection.length === 0) {
      setLanguageMessage({ type: 'error', text: t('voice.errors.atLeastOne') });
      return;
    }

    setIsSavingLanguages(true);
    try {
      const updated = await updateLanguagePreferences(newSelection);
      setSelectedLanguages(updated);
      setLanguageMessage({ type: 'success', text: t('voice.success.languagesUpdated') });
      // Clear success message after 3 seconds
      setTimeout(() => setLanguageMessage(null), 3000);
    } catch (error) {
      console.error('Failed to update language preferences:', error);
      setLanguageMessage({ type: 'error', text: t('voice.errors.updateFailed') });
    } finally {
      setIsSavingLanguages(false);
    }
  };

  const handlePreferredLanguageChange = async (langCode: string) => {
    setPreferredLanguageMessage(null);
    setIsSavingPreferredLanguage(true);
    try {
      const updated = await updatePreferredLanguage(langCode);
      setPreferredLanguage(updated);
      setPreferredLanguageMessage({ type: 'success', text: t('voice.success.defaultUpdated') });
      // Clear success message after 3 seconds
      setTimeout(() => setPreferredLanguageMessage(null), 3000);
    } catch (error) {
      console.error('Failed to update preferred language:', error);
      setPreferredLanguageMessage({ type: 'error', text: 'Failed to update default language' });
    } finally {
      setIsSavingPreferredLanguage(false);
    }
  };

  // Auto-correct preferred language if it's removed from supported languages
  useEffect(() => {
    if (selectedLanguages.length > 0 && !selectedLanguages.includes(preferredLanguage)) {
      handlePreferredLanguageChange(selectedLanguages[0]);
    }
  }, [selectedLanguages, preferredLanguage]);

  const handleSaveBillingInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingBilling(true);
    setBillingMessage(null);

    try {
      const updates: BillingInfoUpdate = {
        company_name: companyName,
        address_line1: addressLine1,
        address_line2: addressLine2,
        city: city,
        postal_code: postalCode,
        country: country,
        vat_number: vatNumber,
        tax_id: taxId
      };

      await updateBillingInfo(updates);
      setBillingMessage({ type: 'success', text: t('billing.success') });
    } catch (error) {
      setBillingMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to update billing information' });
    } finally {
      setIsSavingBilling(false);
    }
  };

  // ============================================================================
  // GDPR HANDLERS (Privacy & Data Protection)
  // Real functionality - compliant with GDPR Articles 15, 17, 20
  // ============================================================================

  const handleConsentChange = async (field: keyof GDPRConsent, value: boolean) => {
    setIsSavingGdpr(true);
    setGdprMessage(null);
    try {
      const updated = await updateGDPRConsents({ [field]: value });
      setGdprConsents(updated);
      setGdprMessage({ type: 'success', text: t('gdpr.success') });
    } catch (error) {
      setGdprMessage({ type: 'error', text: error instanceof Error ? error.message : t('gdpr.errors.updateFailed') });
    } finally {
      setIsSavingGdpr(false);
    }
  };

  const handleExportData = async () => {
    setIsExportingData(true);
    setGdprMessage(null);
    try {
      const exportResult = await requestGDPRDataExport({ format: 'json', include_voice_history: true, include_usage_logs: true });

      // Add to exports list
      setGdprExports(prev => [{
        export_id: exportResult.export_id,
        status: exportResult.status,
        format: exportResult.format,
        requested_at: exportResult.requested_at,
        completed_at: exportResult.completed_at || undefined,
        expires_at: exportResult.expires_at || undefined,
        file_size_bytes: exportResult.file_size_bytes || undefined,
        download_url: exportResult.download_url || undefined
      }, ...prev]);

      if (exportResult.status === 'ready' && exportResult.download_url) {
        // Export is ready immediately (shouldn't happen with new backend but handle it)
        setGdprMessage({ type: 'success', text: t('gdpr.dataExport.success.immediate') });
        window.open(`/api${exportResult.download_url}`, '_blank');
      } else {
        // Export is being processed in background
        setGdprMessage({
          type: 'success',
          text: t('gdpr.dataExport.success.processing')
        });

        // Start polling for status updates
        pollExportStatus(exportResult.export_id);
      }
    } catch (error) {
      setGdprMessage({ type: 'error', text: error instanceof Error ? error.message : t('gdpr.errors.exportFailed') });
    } finally {
      setIsExportingData(false);
    }
  };

  // Poll for export status updates
  const pollExportStatus = async (exportId: string) => {
    const maxAttempts = 30; // Poll for up to 5 minutes (30 * 10 seconds)
    let attempts = 0;

    const checkStatus = async () => {
      try {
        const exports = await getGDPRDataExports();
        const exportItem = exports.exports?.find(e => e.export_id === exportId);

        if (exportItem) {
          // Update the exports list
          setGdprExports(prev => prev.map(e =>
            e.export_id === exportId ? {
              ...e,
              status: exportItem.status,
              completed_at: exportItem.completed_at,
              expires_at: exportItem.expires_at,
              file_size_bytes: exportItem.file_size_bytes,
              download_url: exportItem.download_url
            } : e
          ));

          if (exportItem.status === 'ready') {
            setGdprMessage({ type: 'success', text: t('gdpr.dataExport.success.ready') });
            return; // Stop polling
          } else if (exportItem.status === 'failed') {
            setGdprMessage({ type: 'error', text: t('gdpr.dataExport.errors.failed') });
            return; // Stop polling
          }
        }

        attempts++;
        if (attempts < maxAttempts && ['pending', 'processing'].includes(exportItem?.status || 'pending')) {
          // Continue polling
          setTimeout(checkStatus, 10000); // Check every 10 seconds
        }
      } catch (error) {
        console.error('Error polling export status:', error);
      }
    };

    // Start polling after a short delay
    setTimeout(checkStatus, 5000);
  };

  const handleRequestDeletion = async () => {
    if (!deletePassword) {
      setGdprMessage({ type: 'error', text: t('gdpr.errors.passwordRequired') });
      return;
    }

    setIsDeletingAccount(true);
    setGdprMessage(null);
    try {
      const result = await requestGDPRAccountDeletion(deletePassword, true, deleteReason || undefined);
      setGdprDeletionRequest(result);
      setShowDeleteConfirm(false);
      setDeletePassword('');
      setDeleteReason('');
      setGdprMessage({ type: 'success', text: t('gdpr.accountDeletion.success') });
    } catch (error) {
      setGdprMessage({ type: 'error', text: error instanceof Error ? error.message : t('gdpr.errors.deletionFailed') });
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const handleCancelDeletion = async () => {
    if (!gdprDeletionRequest) return;

    setIsSavingGdpr(true);
    setGdprMessage(null);
    try {
      await cancelGDPRDeletionRequest(gdprDeletionRequest.request_id);
      setGdprDeletionRequest(null);
      setGdprMessage({ type: 'success', text: t('gdpr.accountDeletion.cancelSuccess') });
    } catch (error) {
      setGdprMessage({ type: 'error', text: error instanceof Error ? error.message : t('gdpr.errors.cancelFailed') });
    } finally {
      setIsSavingGdpr(false);
    }
  };

  // ============================================================================
  // REAL HANDLERS FOR CALENDAR APP (CalDAV Sync)
  // These connect to real CalDAV servers via the sync API
  // ============================================================================
  const handleOpenAddCalendar = () => {
    setNewAccountForm({
      account_type: 'caldav',
      display_name: '',
      server_host: '',
      server_port: 443,
      username: '',
      password: '',
      use_ssl: true,
      is_enabled: true,
    });
    setAddAccountModal({ isOpen: true, accountType: 'caldav' });
  };

  const handleDisconnectCalendar = async (id: string) => {
    try {
      await deleteSyncAccount(id);
      setCalendarAccounts(calendarAccounts.filter(acc => acc.id !== id));
      setCalMessage({ type: 'success', text: t('calendarSettings.caldavAccounts.success') });
    } catch (error) {
      console.error('Failed to disconnect calendar:', error);
      setCalMessage({ type: 'error', text: 'Failed to disconnect calendar account' });
    }
  };

  const handleSyncCalendar = async (id: string) => {
    setIsSyncingCal(id);
    try {
      const result = await triggerSync(id);
      if (result.success) {
        setCalMessage({ type: 'success', text: result.message });
        // Reload accounts to get updated sync status
        const accounts = await getSyncAccounts('caldav');
        setCalendarAccounts(accounts);
      } else {
        setCalMessage({ type: 'error', text: result.message });
      }
    } catch (error) {
      console.error('Failed to sync calendar:', error);
      setCalMessage({ type: 'error', text: 'Failed to sync calendar' });
    } finally {
      setIsSyncingCal(null);
    }
  };

  const handleSaveCalendarSettings = (e: React.FormEvent) => {
    e.preventDefault();
    // These settings are stored locally for now
    setCalMessage({ type: 'success', text: t('calendarSettings.syncSettings.success') });
  };

  // ============================================================================
  // REAL HANDLERS FOR EMAIL APP (IMAP/SMTP Sync)
  // These connect to real IMAP/SMTP servers via the sync API
  // ============================================================================
  const handleOpenAddEmail = () => {
    setShowAddEmailModal(true);
  };

  const handleDisconnectEmail = async (id: string) => {
    // Find account for confirmation message
    const account = emailAccounts.find(a => a.id === id);
    const accountName = account?.display_name || account?.email || 'this account';

    // Require confirmation
    if (!confirm(t('emailSettings.imapAccounts.removeConfirm', { accountName }))) {
      return;
    }

    try {
      await emailApi.accounts.delete(id);
      setEmailAccounts(emailAccounts.filter(acc => acc.id !== id));
      setEmailMessage({ type: 'success', text: t('emailSettings.imapAccounts.removeSuccess') });
    } catch (error) {
      console.error('Failed to remove email account:', error);
      setEmailMessage({ type: 'error', text: t('emailSettings.imapAccounts.removeFailed') });
    }
  };

  const handleSyncEmail = async (id: string) => {
    setIsSyncingEmail(id);
    try {
      await emailApi.sync.trigger(id);
      setEmailMessage({ type: 'success', text: t('emailSettings.imapAccounts.syncStarted') });
      // Reload accounts to get updated sync status
      const accounts = await emailApi.accounts.list();
      setEmailAccounts(accounts);
    } catch (error) {
      console.error('Failed to sync email:', error);
      setEmailMessage({ type: 'error', text: t('emailSettings.imapAccounts.syncFailed') });
    } finally {
      setIsSyncingEmail(null);
    }
  };

  const handleSaveEmailSettings = (e: React.FormEvent) => {
    e.preventDefault();
    // These settings are stored locally for now
    setEmailMessage({ type: 'success', text: t('emailSettings.signature.success') });
  };

  // ============================================================================
  // SHARED HANDLERS FOR SYNC ACCOUNT MODAL
  // ============================================================================
  const handleCloseAddAccountModal = () => {
    setAddAccountModal({ isOpen: false, accountType: null });
  };

  const handleAddAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsAddingAccount(true);

    try {
      // First test the connection
      const testResult = await createSyncAccount(newAccountForm);

      if (addAccountModal.accountType === 'caldav') {
        setCalendarAccounts([...calendarAccounts, testResult]);
        setCalMessage({ type: 'success', text: 'CalDAV account added successfully' });
      }
      // Note: IMAP accounts are now handled by EmailAccountSetup component

      handleCloseAddAccountModal();
    } catch (error) {
      console.error('Failed to add account:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to add account';
      setCalMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsAddingAccount(false);
    }
  };

  // ============================================================================
  // DUMMY HANDLERS FOR PHONE APP
  // These simulate Twilio integration but don't actually connect
  // ============================================================================
  const handleConnectTwilio = (e: React.FormEvent) => {
    e.preventDefault();
    if (twilioAccountSid && twilioAuthToken) {
      // DUMMY: Simulates Twilio connection
      setTwilioConnected(true);
      setPhoneMessage({ type: 'success', text: t('phoneSettings.twilio.connectSuccess') });
    } else {
      setPhoneMessage({ type: 'error', text: t('phoneSettings.twilio.errors.missingFields') });
    }
  };

  const handleDisconnectTwilio = () => {
    setTwilioConnected(false);
    setTwilioAccountSid('');
    setTwilioAuthToken('');
    setPhoneMessage({ type: 'success', text: 'Twilio account disconnected' });
  };

  const handleAddPhoneNumber = () => {
    // DUMMY: Simulates adding a phone number
    const newNumber: DummyPhoneNumber = {
      id: `phone-${Date.now()}`,
      number: '+1 (555) ' + Math.floor(100 + Math.random() * 900) + '-' + Math.floor(1000 + Math.random() * 9000),
      country: 'US',
      type: 'local',
      status: 'pending',
    };
    setPhoneNumbers([...phoneNumbers, newNumber]);
    setPhoneMessage({ type: 'success', text: t('phoneSettings.phoneNumbers.provisioningSuccess') });
    // Simulate activation after delay
    setTimeout(() => {
      setPhoneNumbers(prev => prev.map(p => p.id === newNumber.id ? { ...p, status: 'active' as const } : p));
    }, 2000);
  };

  const handleRemovePhoneNumber = (id: string) => {
    setPhoneNumbers(phoneNumbers.filter(p => p.id !== id));
    setPhoneMessage({ type: 'success', text: t('phoneSettings.phoneNumbers.releaseSuccess') });
  };

  const handleSavePhoneSettings = (e: React.FormEvent) => {
    e.preventDefault();
    // DUMMY: Just shows success message
    setPhoneMessage({ type: 'success', text: t('phoneSettings.voicemail.success') });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Get current app's accent color
  const currentApp = apps.find(a => a.id === activeApp)!;
  const accentColor = currentApp.color.replace('text-', '');

  if (isLoadingProfile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="bg-gray-50" style={{ minHeight: 'calc(100vh - var(--health-banner-height, 0px))' }}>
      {/* Header - hidden in employee mode (EmployeeHeader provides this) */}
      {!isEmployeeMode && (
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-4">
              {/* Left - Navigation */}
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/voice')}
                  className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <span className="hidden sm:inline text-sm font-medium">{t('header.back')}</span>
                </button>
                <div className="h-6 w-px bg-gray-300" />
                <div>
                  <h1 className="text-xl font-bold text-gray-900">{t('header.title')}</h1>
                  <p className="text-xs text-gray-500">{t('header.subtitle')}</p>
                </div>
              </div>

              {/* Right - User Menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold">
                    {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <svg
                    className={`w-4 h-4 text-gray-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
                    <button
                      onClick={() => { setUserMenuOpen(false); navigate('/billing'); }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      {t('userMenu.billing')}
                    </button>
                    <button
                      onClick={() => { setUserMenuOpen(false); navigate('/support'); }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      {t('userMenu.support')}
                    </button>
                    {user?.role === 'admin' && (
                      <button
                        onClick={() => { setUserMenuOpen(false); navigate('/admin'); }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-purple-700 hover:bg-purple-50"
                      >
                        {t('userMenu.adminPanel')}
                      </button>
                    )}
                    <div className="border-t border-gray-100 my-1" />
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      {t('userMenu.logout')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* App Tabs - uses CSS variable from HealthBanner for proper positioning */}
      <div
        className="bg-white border-b border-gray-200 sticky z-30"
        style={{ top: 'var(--health-banner-height, 0px)' }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 py-2 overflow-x-auto">
            {apps.map((app) => (
              <button
                key={app.id}
                onClick={() => setActiveApp(app.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all whitespace-nowrap ${
                  activeApp === app.id
                    ? `${app.bgColor} ${app.color}`
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={app.icon} />
                </svg>
                {getAppLabel(app)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Floating Navigation Panel */}
      <div className="fixed left-4 top-1/2 -translate-y-1/2 z-40 hidden lg:block">
        <div className={`bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200 transition-all duration-300 ${isNavCollapsed ? 'w-14' : 'w-44'}`}>
          {/* Collapse toggle */}
          <button
            onClick={() => setIsNavCollapsed(!isNavCollapsed)}
            className="absolute -right-3 top-4 w-6 h-6 bg-white rounded-full shadow-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
          >
            <svg
              className={`w-3 h-3 text-gray-500 transition-transform ${isNavCollapsed ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Navigation items */}
          <div className="p-2 space-y-1">
            {sectionsByApp[activeApp].map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                  activeSection === section.id
                    ? `${currentApp.bgColor} ${currentApp.color}`
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
                title={isNavCollapsed ? getSectionLabel(section) : undefined}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={section.icon} />
                </svg>
                {!isNavCollapsed && (
                  <span className="text-sm font-medium truncate">{getSectionLabel(section)}</span>
                )}
                {activeSection === section.id && !isNavCollapsed && (
                  <div className={`ml-auto w-1.5 h-1.5 rounded-full bg-${accentColor}`} />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

        {/* ================================================================== */}
        {/* MAIA APP SETTINGS */}
        {/* ================================================================== */}
        {activeApp === 'maia' && (
          <>
            {/* Profile Section */}
            <div id="profile" className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">{t('profile.title')}</h2>
                <p className="text-sm text-gray-500">{t('profile.subtitle')}</p>
              </div>
              <form onSubmit={handleSaveProfile} className="p-6 space-y-4">
                {/* Profile Picture */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">{t('profile.title', 'Profile Picture')}</label>
                  <div className="flex items-center gap-4">
                    <UserAvatar
                      user={{
                        id: profile?.id || 0,
                        full_name: profile?.full_name,
                        username: profile?.username,
                        avatar_url: profile?.avatar_url,
                      }}
                      size="xl"
                    />
                    <div className="flex flex-col gap-2">
                      <button
                        type="button"
                        onClick={() => avatarFileInputRef.current?.click()}
                        disabled={isUploadingAvatar}
                        className="px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 disabled:opacity-50 transition-colors"
                      >
                        {isUploadingAvatar ? 'Uploading...' : 'Change Photo'}
                      </button>
                      {profile?.avatar_url && (
                        <button
                          type="button"
                          onClick={handleAvatarDelete}
                          disabled={isUploadingAvatar}
                          className="text-sm text-gray-500 hover:text-red-600 disabled:opacity-50 transition-colors"
                        >
                          Remove
                        </button>
                      )}
                      <p className="text-xs text-gray-400">JPG, PNG or WebP. Max 5MB.</p>
                    </div>
                    <input
                      ref={avatarFileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleAvatarSelect}
                      className="hidden"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.username')}</label>
                  <input
                    type="text"
                    value={profile?.username || ''}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('profile.usernameDisabledHint')}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.fullName')}</label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.email')}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                {profileMessage && (
                  <div className={`p-3 rounded-lg text-sm ${profileMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {profileMessage.text}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={isSavingProfile}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSavingProfile ? t('profile.saving') : t('profile.saveChanges')}
                </button>
              </form>
            </div>

            {/* Interface Language Section */}
            <div id="interface-language" className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">{t('interfaceLanguage.title')}</h2>
                <p className="text-sm text-gray-500">{t('interfaceLanguage.subtitle')}</p>
              </div>
              <div className="p-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('interfaceLanguage.label')}</label>
                <select
                  value={currentLanguage}
                  onChange={(e) => changeLanguage(e.target.value)}
                  className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="en">{t('interfaceLanguage.options.en')}</option>
                  <option value="it">{t('interfaceLanguage.options.it')}</option>
                </select>
              </div>
            </div>

            {/* Password Section */}
            <div id="password" className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">{t('password.title')}</h2>
                <p className="text-sm text-gray-500">{t('password.subtitle')}</p>
              </div>
              <form onSubmit={handleChangePassword} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('password.currentPassword')}</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('password.newPassword')}</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                    minLength={8}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('password.confirmPassword')}</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                    minLength={8}
                  />
                </div>
                {passwordMessage && (
                  <div className={`p-3 rounded-lg text-sm ${passwordMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {passwordMessage.text}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={isSavingPassword}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSavingPassword ? t('password.changing') : t('password.changeButton')}
                </button>
              </form>
            </div>

            {/* Voice Preferences Section */}
            <div id="voice" className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">{t('voice.title')}</h2>
                <p className="text-sm text-gray-500">{t('voice.subtitle')}</p>
              </div>
              <div className="p-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">{t('voice.noiseReduction')}</label>
                <div className="space-y-2">
                  {[
                    { value: 'none' as NoiseReductionType, label: t('voice.noiseOptions.none'), desc: t('voice.noiseOptions.noneDesc') },
                    { value: 'near_field' as NoiseReductionType, label: t('voice.noiseOptions.headset'), desc: t('voice.noiseOptions.headsetDesc') },
                    { value: 'far_field' as NoiseReductionType, label: t('voice.noiseOptions.public'), desc: t('voice.noiseOptions.publicDesc') },
                  ].map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                        noiseReduction === option.value
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      } ${isSavingPreferences ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        type="radio"
                        name="noise_reduction"
                        value={option.value}
                        checked={noiseReduction === option.value}
                        onChange={() => handleNoiseReductionChange(option.value)}
                        disabled={isSavingPreferences}
                        className="sr-only"
                      />
                      <div className="flex-1">
                        <span className="font-medium text-gray-900">{option.label}</span>
                        <p className="text-sm text-gray-500">{option.desc}</p>
                      </div>
                      {noiseReduction === option.value && (
                        <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </label>
                  ))}
                </div>

                {/* Language Preferences */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('voice.languageSupport')}</label>
                  <p className="text-sm text-gray-500 mb-4">
                    {t('voice.languageSupportDesc', { brandName })}
                  </p>
                  {languageMessage && (
                    <div className={`mb-4 p-3 rounded-lg text-sm ${
                      languageMessage.type === 'success'
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                      {languageMessage.text}
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {availableLanguages.map((lang) => (
                      <label
                        key={lang.code}
                        className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedLanguages.includes(lang.code)
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:bg-gray-50'
                        } ${isSavingLanguages ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedLanguages.includes(lang.code)}
                          onChange={() => handleLanguageToggle(lang.code)}
                          disabled={isSavingLanguages}
                          className="sr-only"
                        />
                        <span className="text-xl mr-2">{lang.flag}</span>
                        <div className="flex-1 min-w-0">
                          <span className="block font-medium text-gray-900 text-sm truncate">{lang.name}</span>
                          <span className="block text-xs text-gray-500 truncate">{lang.native}</span>
                        </div>
                        {selectedLanguages.includes(lang.code) && (
                          <svg className="w-4 h-4 text-purple-600 flex-shrink-0 ml-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </label>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-gray-400">
                    {t('voice.selectedLanguages', { count: selectedLanguages.length })}
                  </p>
                </div>

                {/* Default Voice Language */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('voice.defaultLanguage')}</label>
                  <p className="text-sm text-gray-500 mb-4">
                    {t('voice.defaultLanguageDesc', { brandName })}
                  </p>
                  {preferredLanguageMessage && (
                    <div className={`mb-4 p-3 rounded-lg text-sm ${
                      preferredLanguageMessage.type === 'success'
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                      {preferredLanguageMessage.text}
                    </div>
                  )}
                  <div className="relative">
                    <select
                      value={preferredLanguage}
                      onChange={(e) => handlePreferredLanguageChange(e.target.value)}
                      disabled={isSavingPreferredLanguage || selectedLanguages.length === 0}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white appearance-none cursor-pointer ${
                        isSavingPreferredLanguage ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {availableLanguages
                        .filter((lang) => selectedLanguages.includes(lang.code))
                        .map((lang) => (
                        <option key={lang.code} value={lang.code}>
                          {lang.flag} {lang.name} ({lang.native})
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      {isSavingPreferredLanguage ? (
                        <svg className="animate-spin h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>

                {/* Talking-head avatar selection (HeyGen catalog) */}
                {user?.avatar_enabled && catalogAvatars.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('voice.avatarSelection', { defaultValue: 'Avatar' })}
                    </label>
                    <p className="text-sm text-gray-500 mb-4">
                      {t('voice.avatarSelectionDesc', {
                        defaultValue: 'Pick the talking-head avatar that appears during voice chat.',
                      })}
                    </p>
                    <select
                      value={selectedCatalogAvatarId ?? ''}
                      onChange={(e) =>
                        handleCatalogAvatarChange(
                          e.target.value === '' ? null : parseInt(e.target.value, 10)
                        )
                      }
                      disabled={isSavingCatalogAvatar}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white ${
                        isSavingCatalogAvatar ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                      }`}
                    >
                      <option value="">
                        {t('voice.avatarDefault', { defaultValue: 'Use default avatar' })}
                      </option>
                      {catalogAvatars.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                          {a.is_default ? ' (default)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Billing Information Section */}
            <div id="billing" className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">{t('billing.title')}</h2>
                <p className="text-sm text-gray-500">{t('billing.subtitle')}</p>
              </div>
              <form onSubmit={handleSaveBillingInfo} className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('billing.companyName')}</label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder={t('billing.companyNamePlaceholder')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('billing.addressLine1')}</label>
                    <input
                      type="text"
                      value={addressLine1}
                      onChange={(e) => setAddressLine1(e.target.value)}
                      placeholder={t('billing.addressLine1Placeholder')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('billing.addressLine2')}</label>
                    <input
                      type="text"
                      value={addressLine2}
                      onChange={(e) => setAddressLine2(e.target.value)}
                      placeholder={t('billing.addressLine2Placeholder')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('billing.city')}</label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder={t('billing.cityPlaceholder')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('billing.postalCode')}</label>
                    <input
                      type="text"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      placeholder={t('billing.postalCodePlaceholder')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('billing.country')}</label>
                    <select
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                    >
                      <option value="Malta">{t('billing.countryOptions.malta')}</option>
                      <option value="Germany">{t('billing.countryOptions.germany')}</option>
                      <option value="Austria">{t('billing.countryOptions.austria')}</option>
                      <option value="Switzerland">{t('billing.countryOptions.switzerland')}</option>
                      <option value="United Kingdom">{t('billing.countryOptions.unitedKingdom')}</option>
                      <option value="United States">{t('billing.countryOptions.unitedStates')}</option>
                      <option value="France">{t('billing.countryOptions.france')}</option>
                      <option value="Italy">{t('billing.countryOptions.italy')}</option>
                      <option value="Spain">{t('billing.countryOptions.spain')}</option>
                      <option value="Netherlands">{t('billing.countryOptions.netherlands')}</option>
                      <option value="Belgium">{t('billing.countryOptions.belgium')}</option>
                      <option value="Other">{t('billing.countryOptions.other')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('billing.vatNumber')}</label>
                    <input
                      type="text"
                      value={vatNumber}
                      onChange={(e) => setVatNumber(e.target.value)}
                      placeholder={t('billing.vatNumberPlaceholder')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('billing.taxId')}</label>
                    <input
                      type="text"
                      value={taxId}
                      onChange={(e) => setTaxId(e.target.value)}
                      placeholder={t('billing.taxIdPlaceholder')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
                {billingMessage && (
                  <div className={`p-3 rounded-lg text-sm ${billingMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {billingMessage.text}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={isSavingBilling}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSavingBilling ? t('billing.saving') : t('billing.saveButton')}
                </button>
              </form>
            </div>

            {/* GDPR Privacy Settings */}
            <div id="gdpr" className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{t('gdpr.title')}</h2>
                    <p className="text-sm text-gray-500">{t('gdpr.subtitle')}</p>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-6">
                {/* GDPR Message */}
                {gdprMessage && (
                  <div className={`p-4 rounded-lg ${gdprMessage.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                    {gdprMessage.text}
                  </div>
                )}

                {/* Consent Management */}
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900 flex items-center gap-2">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('gdpr.consentManagement')}
                  </h3>
                  <p className="text-sm text-gray-500">{t('gdpr.consentDesc')}</p>

                  <div className="space-y-3">
                    {/* Marketing Emails */}
                    <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                      <div>
                        <span className="font-medium text-gray-900">{t('gdpr.marketingEmails')}</span>
                        <p className="text-sm text-gray-500">{t('gdpr.marketingDesc')}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={gdprConsents?.marketing_emails || false}
                        onChange={(e) => handleConsentChange('marketing_emails', e.target.checked)}
                        disabled={isSavingGdpr}
                        className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                      />
                    </label>

                    {/* Analytics */}
                    <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                      <div>
                        <span className="font-medium text-gray-900">{t('gdpr.analyticsTracking')}</span>
                        <p className="text-sm text-gray-500">{t('gdpr.analyticsDesc')}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={gdprConsents?.analytics_tracking ?? true}
                        onChange={(e) => handleConsentChange('analytics_tracking', e.target.checked)}
                        disabled={isSavingGdpr}
                        className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                      />
                    </label>

                    {/* Third Party Sharing */}
                    <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                      <div>
                        <span className="font-medium text-gray-900">{t('gdpr.thirdPartySharing')}</span>
                        <p className="text-sm text-gray-500">{t('gdpr.thirdPartyDesc')}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={gdprConsents?.third_party_sharing || false}
                        onChange={(e) => handleConsentChange('third_party_sharing', e.target.checked)}
                        disabled={isSavingGdpr}
                        className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                      />
                    </label>

                    {/* Personalization */}
                    <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                      <div>
                        <span className="font-medium text-gray-900">{t('gdpr.personalization')}</span>
                        <p className="text-sm text-gray-500">{t('gdpr.personalizationDesc')}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={gdprConsents?.personalization ?? true}
                        onChange={(e) => handleConsentChange('personalization', e.target.checked)}
                        disabled={isSavingGdpr}
                        className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                      />
                    </label>

                    {/* Voice Data Retention */}
                    <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                      <div>
                        <span className="font-medium text-gray-900">{t('gdpr.voiceHistory')}</span>
                        <p className="text-sm text-gray-500">{t('gdpr.voiceHistoryDesc')}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={gdprConsents?.voice_data_retention ?? true}
                        onChange={(e) => handleConsentChange('voice_data_retention', e.target.checked)}
                        disabled={isSavingGdpr}
                        className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                      />
                    </label>
                  </div>
                </div>

                {/* Data Export - Article 20 */}
                <div className="pt-4 border-t border-gray-200 space-y-4">
                  <h3 className="font-medium text-gray-900 flex items-center gap-2">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    {t('gdpr.dataExport.title')}
                  </h3>
                  <p className="text-sm text-gray-500">{t('gdpr.dataExport.desc')}</p>

                  <button
                    onClick={handleExportData}
                    disabled={isExportingData}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isExportingData ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {t('gdpr.dataExport.exporting')}
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        {t('gdpr.dataExport.button')}
                      </>
                    )}
                  </button>

                  {/* Export History */}
                  {gdprExports.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <h4 className="text-sm font-medium text-gray-700">{t('gdpr.dataExport.recentExports')}</h4>
                      {gdprExports.slice(0, 3).map((exp) => (
                        <div key={exp.export_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-sm">
                          <div>
                            <span className="font-medium text-gray-900">{new Date(exp.requested_at).toLocaleDateString()}</span>
                            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                              exp.status === 'ready' ? 'bg-green-100 text-green-800' :
                              exp.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {t(`gdpr.dataExport.status.${exp.status}`, { defaultValue: exp.status })}
                            </span>
                          </div>
                          {exp.status === 'ready' && exp.download_url && (
                            <a
                              href={`/api${exp.download_url}`}
                              className="text-purple-600 hover:text-purple-700 font-medium"
                              download
                            >
                              {t('gdpr.dataExport.download')}
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Account Deletion - Article 17 */}
                <div className="pt-4 border-t border-gray-200 space-y-4">
                  <h3 className="font-medium text-red-600 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    {t('gdpr.accountDeletion.title')}
                  </h3>

                  {gdprDeletionRequest ? (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3">
                      <div className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div>
                          <p className="font-medium text-red-800">{t('gdpr.accountDeletion.scheduled.title')}</p>
                          <p className="text-sm text-red-700">
                            {t('gdpr.accountDeletion.scheduled.message', { date: new Date(gdprDeletionRequest.scheduled_deletion_date).toLocaleDateString() })}
                          </p>
                          <p className="text-sm text-red-600 mt-1">
                            {t('gdpr.accountDeletion.scheduled.note')}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleCancelDeletion}
                        disabled={isSavingGdpr}
                        className="px-4 py-2 bg-white text-red-600 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isSavingGdpr ? t('gdpr.accountDeletion.scheduled.cancelling') : t('gdpr.accountDeletion.scheduled.cancelButton')}
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-gray-500">
                        {t('gdpr.accountDeletion.desc')}
                      </p>

                      {isInstitutionalTenant ? (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                          {t('gdpr.accountDeletion.managedByOrganization')}
                        </div>
                      ) : !showDeleteConfirm ? (
                        <button
                          onClick={() => setShowDeleteConfirm(true)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                          {t('gdpr.accountDeletion.requestButton')}
                        </button>
                      ) : (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-4">
                          <div className="flex items-start gap-3">
                            <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <div>
                              <p className="font-medium text-red-800">{t('gdpr.accountDeletion.confirmDialog.title')}</p>
                              <p className="text-sm text-red-700">{t('gdpr.accountDeletion.confirmDialog.subtitle')}</p>
                            </div>
                          </div>

                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('gdpr.accountDeletion.confirmDialog.passwordLabel')}
                              </label>
                              <input
                                type="password"
                                value={deletePassword}
                                onChange={(e) => setDeletePassword(e.target.value)}
                                placeholder={t('gdpr.accountDeletion.confirmDialog.passwordPlaceholder')}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('gdpr.accountDeletion.confirmDialog.reasonLabel')}
                              </label>
                              <textarea
                                value={deleteReason}
                                onChange={(e) => setDeleteReason(e.target.value)}
                                placeholder={t('gdpr.accountDeletion.confirmDialog.reasonPlaceholder')}
                                rows={2}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                              />
                            </div>
                          </div>

                          <div className="flex gap-3">
                            <button
                              onClick={handleRequestDeletion}
                              disabled={isDeletingAccount || !deletePassword}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {isDeletingAccount ? t('gdpr.accountDeletion.confirmDialog.confirming') : t('gdpr.accountDeletion.confirmDialog.confirmButton')}
                            </button>
                            <button
                              onClick={() => {
                                setShowDeleteConfirm(false);
                                setDeletePassword('');
                                setDeleteReason('');
                              }}
                              className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              {t('gdpr.accountDeletion.confirmDialog.cancelButton')}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Legal Info */}
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-400">
                    {t('gdpr.legal', { email: privacyEmail })}
                  </p>
                </div>
              </div>
            </div>

            {/* Account Info */}
            <div id="account" className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">{t('account.title')}</h2>
              </div>
              <div className="p-6 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('account.accountType')}</span>
                  <span className="font-medium text-gray-900 capitalize">{profile?.role}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('account.memberSince')}</span>
                  <span className="font-medium text-gray-900">
                    {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : '-'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('account.lastLogin')}</span>
                  <span className="font-medium text-gray-900">
                    {profile?.last_login ? new Date(profile.last_login).toLocaleString() : t('account.never')}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ================================================================== */}
        {/* CALENDAR APP SETTINGS (DUMMY) */}
        {/* ================================================================== */}
        {activeApp === 'calendar' && (
          <>
            {/* Connected CalDAV Accounts */}
            <div id="cal-accounts" className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">{t('calendarSettings.caldavAccounts.title')}</h2>
                <p className="text-sm text-gray-500">{t('calendarSettings.caldavAccounts.subtitle')}</p>
              </div>
              <div className="p-6 space-y-4">
                {isLoadingCalAccounts ? (
                  <div className="flex items-center justify-center py-8">
                    <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : calendarAccounts.length === 0 ? (
                  <div className="text-center py-8">
                    <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="mt-2 text-gray-500">{t('calendarSettings.caldavAccounts.emptyTitle')}</p>
                    <p className="text-sm text-gray-400">{t('calendarSettings.caldavAccounts.emptySubtitle')}</p>
                  </div>
                ) : (
                  calendarAccounts.map((account) => (
                    <div key={account.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-100">
                          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{account.display_name}</div>
                          <div className="text-sm text-gray-500">{account.server_host}:{account.server_port}</div>
                          <div className={`text-xs ${account.last_sync_status === 'success' ? 'text-green-600' : account.last_sync_status === 'failed' ? 'text-red-600' : 'text-gray-500'}`}>
                            {account.last_sync_at ? `Last synced: ${new Date(account.last_sync_at).toLocaleString()}` : 'Never synced'}
                            {account.last_sync_status === 'failed' && account.last_sync_error && (
                              <span className="ml-1 text-red-500">({account.last_sync_error})</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSyncCalendar(account.id)}
                          disabled={isSyncingCal === account.id}
                          className="text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
                        >
                          {isSyncingCal === account.id ? t('calendarSettings.caldavAccounts.syncing') : t('calendarSettings.caldavAccounts.syncNow')}
                        </button>
                        <button
                          onClick={() => handleDisconnectCalendar(account.id)}
                          className="text-sm text-red-600 hover:text-red-700"
                        >
                          {t('calendarSettings.caldavAccounts.disconnect')}
                        </button>
                      </div>
                    </div>
                  ))
                )}

                {/* Add CalDAV Account Button */}
                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={handleOpenAddCalendar}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('calendarSettings.caldavAccounts.addButton')}
                  </button>
                  <p className="mt-2 text-xs text-gray-500">
                    {t('calendarSettings.caldavAccounts.addButtonHint')}
                  </p>
                </div>

                {calMessage && (
                  <div className={`p-3 rounded-lg text-sm ${calMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {calMessage.text}
                  </div>
                )}
              </div>
            </div>

            {/* Sync Settings */}
            <div id="cal-sync" className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">{t('calendarSettings.syncSettings.title')}</h2>
                <p className="text-sm text-gray-500">{t('calendarSettings.syncSettings.subtitle')}</p>
              </div>
              <form onSubmit={handleSaveCalendarSettings} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('calendarSettings.syncSettings.syncInterval')}</label>
                  <select
                    value={calSyncInterval}
                    onChange={(e) => setCalSyncInterval(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    <option value="5">{t('calendarSettings.syncSettings.syncOptions.every5')}</option>
                    <option value="15">{t('calendarSettings.syncSettings.syncOptions.every15')}</option>
                    <option value="30">{t('calendarSettings.syncSettings.syncOptions.every30')}</option>
                    <option value="60">{t('calendarSettings.syncSettings.syncOptions.everyHour')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('calendarSettings.syncSettings.defaultDuration')}</label>
                  <select
                    value={calDefaultDuration}
                    onChange={(e) => setCalDefaultDuration(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    <option value="15">{t('calendarSettings.syncSettings.durationOptions.15')}</option>
                    <option value="30">{t('calendarSettings.syncSettings.durationOptions.30')}</option>
                    <option value="45">{t('calendarSettings.syncSettings.durationOptions.45')}</option>
                    <option value="60">{t('calendarSettings.syncSettings.durationOptions.60')}</option>
                  </select>
                </div>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('calendarSettings.syncSettings.saveButton')}
                </button>
              </form>
            </div>

            {/* Notification Settings */}
            <div id="cal-notifications" className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">{t('calendarSettings.notifications.title')}</h2>
                <p className="text-sm text-gray-500">{t('calendarSettings.notifications.subtitle')}</p>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('calendarSettings.notifications.defaultReminder')}</label>
                  <select
                    value={calNotifyBefore}
                    onChange={(e) => setCalNotifyBefore(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    <option value="5">{t('calendarSettings.notifications.reminderOptions.5')}</option>
                    <option value="10">{t('calendarSettings.notifications.reminderOptions.10')}</option>
                    <option value="15">{t('calendarSettings.notifications.reminderOptions.15')}</option>
                    <option value="30">{t('calendarSettings.notifications.reminderOptions.30')}</option>
                    <option value="60">{t('calendarSettings.notifications.reminderOptions.60')}</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Availability Settings */}
            <div id="cal-availability" className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">{t('calendarSettings.availability.title')}</h2>
                <p className="text-sm text-gray-500">{t('calendarSettings.availability.subtitle')}</p>
              </div>
              <form onSubmit={handleSaveCalendarSettings} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('calendarSettings.availability.startTime')}</label>
                    <input
                      type="time"
                      value={calWorkingHoursStart}
                      onChange={(e) => setCalWorkingHoursStart(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('calendarSettings.availability.endTime')}</label>
                    <input
                      type="time"
                      value={calWorkingHoursEnd}
                      onChange={(e) => setCalWorkingHoursEnd(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('calendarSettings.availability.saveButton')}
                </button>
              </form>
            </div>
          </>
        )}

        {/* ================================================================== */}
        {/* EMAIL APP SETTINGS (DUMMY) */}
        {/* ================================================================== */}
        {activeApp === 'email' && (
          <>
            {/* Connected IMAP Accounts */}
            <div id="email-accounts" className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">{t('emailSettings.imapAccounts.title')}</h2>
                <p className="text-sm text-gray-500">{t('emailSettings.imapAccounts.subtitle')}</p>
              </div>
              <div className="p-6 space-y-4">
                {isLoadingEmailAccounts ? (
                  <div className="flex items-center justify-center py-8">
                    <svg className="animate-spin h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : emailAccounts.length === 0 ? (
                  <div className="text-center py-8">
                    <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <p className="mt-2 text-gray-500">{t('emailSettings.imapAccounts.emptyTitle')}</p>
                    <p className="text-sm text-gray-400">{t('emailSettings.imapAccounts.emptySubtitle')}</p>
                  </div>
                ) : (
                  emailAccounts.map((account) => (
                    <div key={account.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-100">
                          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{account.display_name || account.email}</div>
                          <div className="text-sm text-gray-500">{account.imap_host}:{account.imap_port}</div>
                          <div className={`text-xs ${
                            account.sync_status === 'active' ? 'text-green-600' :
                            account.sync_status === 'error' ? 'text-red-600' :
                            account.sync_status === 'syncing' ? 'text-blue-600' :
                            'text-gray-500'
                          }`}>
                            {account.last_sync_at
                              ? `Last synced: ${new Date(account.last_sync_at).toLocaleString()}`
                              : 'Never synced'}
                            {account.sync_status === 'error' && account.last_error && (
                              <span className="ml-1 text-red-500">({account.last_error})</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSyncEmail(account.id)}
                          disabled={isSyncingEmail === account.id}
                          className="text-sm text-green-600 hover:text-green-700 disabled:opacity-50"
                        >
                          {isSyncingEmail === account.id ? t('emailSettings.imapAccounts.syncing') : t('emailSettings.imapAccounts.syncNow')}
                        </button>
                        <button
                          onClick={() => handleDisconnectEmail(account.id)}
                          className="text-sm text-red-600 hover:text-red-700"
                        >
                          {t('emailSettings.imapAccounts.remove')}
                        </button>
                      </div>
                    </div>
                  ))
                )}

                {/* Add IMAP Account Button */}
                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={handleOpenAddEmail}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('emailSettings.imapAccounts.addButton')}
                  </button>
                  <p className="mt-2 text-xs text-gray-500">
                    {t('emailSettings.imapAccounts.addButtonHint')}
                  </p>
                </div>

                {emailMessage && (
                  <div className={`p-3 rounded-lg text-sm ${emailMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {emailMessage.text}
                  </div>
                )}
              </div>
            </div>

            {/* IMAP/SMTP Settings */}
            <div id="email-imap" className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">{t('emailSettings.serverSettings.title')}</h2>
                <p className="text-sm text-gray-500">{t('emailSettings.serverSettings.subtitle')}</p>
              </div>
              <div className="p-6">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                  <div className="flex items-center gap-2 font-medium mb-1">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {t('emailSettings.serverSettings.oauthRecommended')}
                  </div>
                  {t('emailSettings.serverSettings.oauthNote')}
                </div>
              </div>
            </div>

            {/* Signatures */}
            <div id="email-signatures" className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">{t('emailSettings.signature.title')}</h2>
                <p className="text-sm text-gray-500">{t('emailSettings.signature.subtitle')}</p>
              </div>
              <form onSubmit={handleSaveEmailSettings} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('emailSettings.signature.label')}</label>
                  <textarea
                    value={emailSignature}
                    onChange={(e) => setEmailSignature(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                    placeholder={t('emailSettings.signature.placeholder')}
                  />
                </div>
                <button
                  type="submit"
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  {t('emailSettings.signature.saveButton')}
                </button>
              </form>
            </div>

            {/* Auto-Reply / Filters */}
            <div id="email-filters" className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">{t('emailSettings.autoReply.title')}</h2>
                <p className="text-sm text-gray-500">{t('emailSettings.autoReply.subtitle')}</p>
              </div>
              <form onSubmit={handleSaveEmailSettings} className="p-6 space-y-4">
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900">{t('emailSettings.autoReply.autoReplyLabel')}</div>
                    <div className="text-sm text-gray-500">{t('emailSettings.autoReply.autoReplyDesc')}</div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={emailAutoReply}
                      onChange={(e) => setEmailAutoReply(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                  </label>
                </div>
                {emailAutoReply && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('emailSettings.autoReply.messageLabel')}</label>
                    <textarea
                      value={emailAutoReplyMessage}
                      onChange={(e) => setEmailAutoReplyMessage(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                      placeholder={t('emailSettings.autoReply.messagePlaceholder')}
                    />
                  </div>
                )}
                <button
                  type="submit"
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  {t('emailSettings.autoReply.saveButton')}
                </button>
              </form>
            </div>
          </>
        )}

        {/* ================================================================== */}
        {/* PHONE APP SETTINGS (DUMMY) */}
        {/* ================================================================== */}
        {activeApp === 'phone' && (
          <>
            {/* Phone Numbers */}
            <div id="phone-numbers" className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">{t('phoneSettings.phoneNumbers.title')}</h2>
                <p className="text-sm text-gray-500">{t('phoneSettings.phoneNumbers.subtitle')}</p>
              </div>
              <div className="p-6 space-y-4">
                {phoneNumbers.map((phone) => (
                  <div key={phone.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                        <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{phone.number}</div>
                        <div className="text-sm text-gray-500">{phone.country} • {phone.type}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        phone.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {phone.status === 'active' ? t('phoneSettings.phoneNumbers.active') : t('phoneSettings.phoneNumbers.pending')}
                      </span>
                      <button
                        onClick={() => handleRemovePhoneNumber(phone.id)}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        {t('phoneSettings.phoneNumbers.release')}
                      </button>
                    </div>
                  </div>
                ))}

                <button
                  onClick={handleAddPhoneNumber}
                  disabled={!twilioConnected}
                  className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-orange-400 hover:text-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('phoneSettings.phoneNumbers.addButton')}
                </button>

                {!twilioConnected && (
                  <p className="text-sm text-amber-600 text-center">{t('phoneSettings.phoneNumbers.addDisabledHint')}</p>
                )}

                {phoneMessage && (
                  <div className={`p-3 rounded-lg text-sm ${phoneMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {phoneMessage.text}
                  </div>
                )}
              </div>
            </div>

            {/* Twilio Connection */}
            <div id="phone-twilio" className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">{t('phoneSettings.twilio.title')}</h2>
                <p className="text-sm text-gray-500">{t('phoneSettings.twilio.subtitle')}</p>
              </div>
              <div className="p-6">
                {twilioConnected ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <div className="font-medium text-green-800">{t('phoneSettings.twilio.connected')}</div>
                        <div className="text-sm text-green-600">{t('phoneSettings.twilio.connectedHint')}</div>
                      </div>
                    </div>
                    <button
                      onClick={handleDisconnectTwilio}
                      className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      {t('phoneSettings.twilio.disconnectButton')}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleConnectTwilio} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('phoneSettings.twilio.accountSid')}</label>
                      <input
                        type="text"
                        value={twilioAccountSid}
                        onChange={(e) => setTwilioAccountSid(e.target.value)}
                        placeholder={t('phoneSettings.twilio.accountSidPlaceholder')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-mono text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('phoneSettings.twilio.authToken')}</label>
                      <input
                        type="password"
                        value={twilioAuthToken}
                        onChange={(e) => setTwilioAuthToken(e.target.value)}
                        placeholder={t('phoneSettings.twilio.authTokenPlaceholder')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2 font-medium mb-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {t('phoneSettings.twilio.whereToFind')}
                      </div>
                      {t('phoneSettings.twilio.whereToFindHint')}
                    </div>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                    >
                      {t('phoneSettings.twilio.connectButton')}
                    </button>
                  </form>
                )}
              </div>
            </div>

            {/* Voicemail Settings */}
            <div id="phone-voicemail" className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">{t('phoneSettings.voicemail.title')}</h2>
                <p className="text-sm text-gray-500">{t('phoneSettings.voicemail.subtitle')}</p>
              </div>
              <form onSubmit={handleSavePhoneSettings} className="p-6 space-y-4">
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900">{t('phoneSettings.voicemail.enableLabel')}</div>
                    <div className="text-sm text-gray-500">{t('phoneSettings.voicemail.enableDesc')}</div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={voicemailEnabled}
                      onChange={(e) => setVoicemailEnabled(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                  </label>
                </div>
                {voicemailEnabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('phoneSettings.voicemail.greetingLabel')}</label>
                    <select
                      value={voicemailGreeting}
                      onChange={(e) => setVoicemailGreeting(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                    >
                      <option value="default">{t('phoneSettings.voicemail.greetingOptions.default')}</option>
                      <option value="name">{t('phoneSettings.voicemail.greetingOptions.name')}</option>
                      <option value="custom">{t('phoneSettings.voicemail.greetingOptions.custom')}</option>
                    </select>
                  </div>
                )}
                <button
                  type="submit"
                  className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  {t('phoneSettings.voicemail.saveButton')}
                </button>
              </form>
            </div>

            {/* Call Routing */}
            <div id="phone-routing" className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">{t('phoneSettings.callRouting.title')}</h2>
                <p className="text-sm text-gray-500">{t('phoneSettings.callRouting.subtitle')}</p>
              </div>
              <form onSubmit={handleSavePhoneSettings} className="p-6 space-y-4">
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900">{t('phoneSettings.callRouting.forwardingLabel')}</div>
                    <div className="text-sm text-gray-500">{t('phoneSettings.callRouting.forwardingDesc')}</div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={callForwardingEnabled}
                      onChange={(e) => setCallForwardingEnabled(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                  </label>
                </div>
                {callForwardingEnabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('phoneSettings.callRouting.forwardToLabel')}</label>
                    <input
                      type="tel"
                      value={callForwardingNumber}
                      onChange={(e) => setCallForwardingNumber(e.target.value)}
                      placeholder={t('phoneSettings.callRouting.forwardToPlaceholder')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                )}
                <button
                  type="submit"
                  className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  {t('phoneSettings.callRouting.saveButton')}
                </button>
              </form>
            </div>
          </>
        )}
      </div>

      {/* Add Sync Account Modal */}
      {addAccountModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  {addAccountModal.accountType === 'caldav' ? t('modals.addAccount.caldavTitle') : t('modals.addAccount.imapTitle')}
                </h2>
                <button
                  onClick={handleCloseAddAccountModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {addAccountModal.accountType === 'caldav'
                  ? t('modals.addAccount.caldavDesc')
                  : t('modals.addAccount.imapDesc')}
              </p>
            </div>

            <form onSubmit={handleAddAccount} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('modals.addAccount.displayName')}</label>
                <input
                  type="text"
                  value={newAccountForm.display_name}
                  onChange={(e) => setNewAccountForm({ ...newAccountForm, display_name: e.target.value })}
                  placeholder={addAccountModal.accountType === 'caldav' ? t('modals.addAccount.displayNamePlaceholderCaldav') : t('modals.addAccount.displayNamePlaceholderImap')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('modals.addAccount.serverHost')}</label>
                <input
                  type="text"
                  value={newAccountForm.server_host}
                  onChange={(e) => setNewAccountForm({ ...newAccountForm, server_host: e.target.value })}
                  placeholder={addAccountModal.accountType === 'caldav' ? t('modals.addAccount.serverHostPlaceholderCaldav') : t('modals.addAccount.serverHostPlaceholderImap')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('modals.addAccount.port')}</label>
                  <input
                    type="number"
                    value={newAccountForm.server_port}
                    onChange={(e) => setNewAccountForm({ ...newAccountForm, server_port: parseInt(e.target.value) || 443 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newAccountForm.use_ssl}
                      onChange={(e) => setNewAccountForm({ ...newAccountForm, use_ssl: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{t('modals.addAccount.useSSL')}</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('modals.addAccount.username')}</label>
                <input
                  type="text"
                  value={newAccountForm.username}
                  onChange={(e) => setNewAccountForm({ ...newAccountForm, username: e.target.value })}
                  placeholder={t('modals.addAccount.usernamePlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('modals.addAccount.password')}</label>
                <input
                  type="password"
                  value={newAccountForm.password}
                  onChange={(e) => setNewAccountForm({ ...newAccountForm, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('modals.addAccount.passwordHint')}
                </p>
              </div>

              {/* Common server presets */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-medium text-gray-600 mb-2">{t('modals.addAccount.quickPresets')}</p>
                <div className="flex flex-wrap gap-2">
                  {addAccountModal.accountType === 'caldav' ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setNewAccountForm({ ...newAccountForm, server_host: 'caldav.fastmail.com', server_port: 443 })}
                        className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50"
                      >
                        Fastmail
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewAccountForm({ ...newAccountForm, server_host: 'www.googleapis.com', server_port: 443 })}
                        className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50"
                      >
                        Google
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => setNewAccountForm({ ...newAccountForm, server_host: 'imap.gmail.com', server_port: 993 })}
                        className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50"
                      >
                        Gmail
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewAccountForm({ ...newAccountForm, server_host: 'outlook.office365.com', server_port: 993 })}
                        className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50"
                      >
                        Outlook
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewAccountForm({ ...newAccountForm, server_host: 'imap.fastmail.com', server_port: 993 })}
                        className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50"
                      >
                        Fastmail
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseAddAccountModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t('modals.addAccount.cancelButton')}
                </button>
                <button
                  type="submit"
                  disabled={isAddingAccount}
                  className={`flex-1 px-4 py-2 rounded-lg text-white transition-colors ${
                    addAccountModal.accountType === 'caldav'
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-green-600 hover:bg-green-700'
                  } disabled:opacity-50`}
                >
                  {isAddingAccount ? t('modals.addAccount.adding') : t('modals.addAccount.addButton')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Email Account Setup Modal (aegis5 - test-first flow) */}
      {showAddEmailModal && (
        <EmailAccountSetup
          onComplete={(account) => {
            setEmailAccounts([...emailAccounts, account]);
            setShowAddEmailModal(false);
            setEmailMessage({ type: 'success', text: t('emailSettings.imapAccounts.success') });
          }}
          onCancel={() => setShowAddEmailModal(false)}
        />
      )}

      {/* Avatar Crop Modal */}
      {showAvatarCrop && selectedAvatarFile && (
        <AvatarCropModal
          imageFile={selectedAvatarFile}
          onComplete={handleAvatarCropComplete}
          onCancel={() => {
            setShowAvatarCrop(false);
            setSelectedAvatarFile(null);
          }}
        />
      )}
    </div>
  );
}
